import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

const API_BASE = process.env.REACT_APP_API_BASE;

const appointmentTypes = [
  { code: 'A1', label: 'HouseHold' },
  { code: 'A2', label: 'Essential Kit' },
  { code: 'A3', label: 'Safety Kit' },
];

const emptyForm = {
  id: null,
  appointmentType: '',
  name: '',
  gender: 'Male',
  mobile: '',
  aadhar: '',
  registrationNo: '',
  vidhansabha: 'Karad South',
  karad: { village: '', ganName: '', zpName: '', reference: '' },
  other: { village: '', taluka: '', district: '' },
  appointmentLocation: '',
  appointmentDate: '',
  godownVisitDate: '',
  godownLocation: '',
  campLocation: '',
  operatorName: '',
  formOwner: '',
  whatsappMobile: '',
  cashAmount: '',
  onlineAmount: '',
  paymentDate: '',
};

// Helper to extract YYYY-MM-DD from any date string
const normalizeDate = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  return dateStr;
};

// Helper to format YYYY-MM-DD to dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (year && month && day) {
    return `${day}/${month}/${year}`;
  }
  return dateStr;
};

export default function Appointment({ userid, user }) {
  const isAdmin = user?.user_role === 'ADMIN';
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({
    appointmentType: '',
    name: '',
    mobile: '',
    aadhar: '',
    registrationNo: '',
    godownVisitDateFrom: '',
    godownVisitDateTo: '',
    campLocation: '',
    formOwner: '',
  });

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await fetch(`${API_BASE}/appointments`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
      const data = await res.json();
      setRecords(data.map(mapFromApi));
    } catch (err) {
      console.error('Error fetching appointments:', err);
    }
  };

  const mapFromApi = (r) => {
    const isKarad = r.vidhansabha === 'Karad South';
    return {
      id: r.id,
      appointmentType: r.appointment_type || '',
      name: r.name || '',
      gender: r.gender || 'Male',
      mobile: r.mobile || '',
      aadhar: r.aadhar || '',
      registrationNo: r.registration_no || '',
      vidhansabha: r.vidhansabha || 'Karad South',
      appointmentLocation: r.appointment_location || '',
      appointmentDate: normalizeDate(r.appointment_date),
      godownVisitDate: normalizeDate(r.godown_visit_date),
      godownLocation: r.godown_location || '',
      campLocation: r.camp_location || '',
      operatorName: r.operator_name || '',
      formOwner: r.form_owner || '',
      whatsappMobile: r.whatsapp_mobile || '',
      cashAmount: r.cash_amount || '',
      onlineAmount: r.online_amount || '',
      paymentDate: normalizeDate(r.payment_date),
      karad: {
        village: isKarad ? r.village || '' : '',
        ganName: r.gan_name || '',
        zpName: r.zp_name || '',
        reference: r.reference || '',
      },
      other: {
        village: !isKarad ? r.village || '' : '',
        taluka: r.taluka || '',
        district: r.district || '',
      },
    };
  };

  const mapToApi = (f, uid) => {
    const isKarad = f.vidhansabha === 'Karad South';
    return {
      userid: uid,
      appointment_type: f.appointmentType,
      name: f.name,
      gender: f.gender,
      mobile: f.mobile,
      aadhar: f.aadhar,
      registration_no: f.registrationNo,
      vidhansabha: f.vidhansabha,
      village: isKarad ? f.karad.village : f.other.village,
      gan_name: isKarad ? f.karad.ganName : null,
      zp_name: isKarad ? f.karad.zpName : null,
      reference: isKarad ? f.karad.reference : null,
      taluka: !isKarad ? f.other.taluka : null,
      district: !isKarad ? f.other.district : null,
      appointment_location: f.appointmentLocation,
      appointment_date: f.appointmentDate || null,
      godown_visit_date: f.godownVisitDate || null,
      godown_location: f.godownLocation,
      camp_location: f.campLocation,
      operator_name: f.operatorName,
      form_owner: f.formOwner,
      whatsapp_mobile: f.whatsappMobile,
      cash_amount: f.cashAmount || null,
      online_amount: f.onlineAmount || null,
      payment_date: f.paymentDate || null,
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isEdit = !!editingId;
    const url = isEdit
      ? `${API_BASE}/appointments/${editingId}`
      : `${API_BASE}/appointments`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const payload = mapToApi(form, userid);
      if (isEdit) payload._user_role = user?.user_role || '';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        fetchRecords();
        setForm(emptyForm);
        setEditingId(null);
        setOpen(false);
      } else {
        const errData = await res.json();
        console.error('Save error:', errData);
        alert('Failed to save record.');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error connecting to server.');
    }
  };

  const handleEdit = (id) => {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }
    setForm(rec);
    setEditingId(id);
    setOpen(true);
  };

  const handleToggleLock = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/appointments/${id}/toggle-lock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ admin_name: user?.namename || 'admin' })
      });
      if (!res.ok) throw new Error('Toggle lock failed');
      await fetchRecords();
    } catch (err) {
      alert('Error toggling lock: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    const rec = records.find((r) => r.id === id);
    if (!rec) return;
    if (!isAdmin && rec.is_locked && !rec.unlock_granted_by) {
      alert('This record is locked. Please ask an admin to unlock it first.');
      return;
    }

    if (!window.confirm('Delete this record?')) return;
    try {
      const res = await fetch(`${API_BASE}/appointments/${id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        fetchRecords();
        if (editingId === id) {
          setForm(emptyForm);
          setEditingId(null);
          setOpen(false);
        }
      } else {
        alert('Failed to delete record.');
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleFilterChange = (e, key) => {
    const v = e.target.value || '';
    setFilters((s) => ({ ...s, [key]: v }));
  };

  const handleGodownDateFromChange = (e) => {
    setFilters((s) => ({ ...s, godownVisitDateFrom: e.target.value }));
  };
  const handleGodownDateToChange = (e) => {
    setFilters((s) => ({ ...s, godownVisitDateTo: e.target.value }));
  };

  // Filter logic
  const filtered = records.filter((r) => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());

    if (filters.appointmentType && r.appointmentType !== filters.appointmentType) return false;
    if (filters.name && !m(r.name).includes(m(filters.name))) return false;
    if (filters.mobile && !m(r.mobile).includes(m(filters.mobile))) return false;
    if (filters.aadhar && !m(r.aadhar).includes(m(filters.aadhar))) return false;
    if (filters.registrationNo && !m(r.registrationNo).includes(m(filters.registrationNo)))
      return false;
    if (filters.campLocation && !m(r.campLocation).includes(m(filters.campLocation)))
      return false;
    if (filters.formOwner && !m(r.formOwner).includes(m(filters.formOwner))) return false;
    if (filters.paymentOption && r.paymentOption !== filters.paymentOption) return false;

    // Godown visit date range
    const recordDate = r.godownVisitDate; // YYYY-MM-DD
    if (filters.godownVisitDateFrom && recordDate < filters.godownVisitDateFrom) return false;
    if (filters.godownVisitDateTo && recordDate > filters.godownVisitDateTo) return false;

    return true;
  });

  const isKarad = form.vidhansabha === 'Karad South';

  const searchInputStyle = {
    bgcolor: 'white',
    borderRadius: 1,
    px: 0.5,
    border: '1px solid black',
  };

  const searchInputProps = { disableUnderline: true };
  const searchInputPropsWithIcon = {
    disableUnderline: true,
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" color="primary" />
      </InputAdornment>
    ),
  };

  const headerCellStyle = {
    bgcolor: '#0b4a6f',
    color: 'white',
    fontWeight: 'bold',
    py: 1.5,
  };

  const filterCellStyle = {
    bgcolor: '#e1f5fe',
    py: 1,
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
            Appointments
          </Typography>
          <Chip
            label={`${records.length} records`}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add Appointment
          </Button>
        </Box>

        <TableContainer
          sx={{
            maxHeight: 'calc(100vh - 200px)',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>Type</TableCell>
                <TableCell sx={headerCellStyle}>Name</TableCell>
                <TableCell sx={headerCellStyle}>Mobile</TableCell>
                <TableCell sx={headerCellStyle}>Aadhar</TableCell>
                <TableCell sx={headerCellStyle}>Registration No</TableCell>
                <TableCell sx={headerCellStyle}>Godown Visit Date</TableCell>
                <TableCell sx={headerCellStyle}>Camp Location</TableCell>
                <TableCell sx={headerCellStyle}>Form Owner</TableCell>
                <TableCell sx={headerCellStyle}>Payment Option</TableCell>
                <TableCell align="right" sx={headerCellStyle}>
                  Actions
                </TableCell>
              </TableRow>
              <TableRow>
                {/* Type filter - dropdown selection */}
                <TableCell sx={filterCellStyle}>
                  <Select
                    size="small"
                    variant="standard"
                    value={filters.appointmentType}
                    onChange={(e) => handleFilterChange(e, 'appointmentType')}
                    sx={{ ...searchInputStyle, minWidth: 100 }}
                    displayEmpty
                  >
                    <MenuItem value="">All</MenuItem>
                    {appointmentTypes.map((a) => (
                      <MenuItem key={a.code} value={a.label}>
                        {a.label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                {/* Name filter */}
                <TableCell sx={filterCellStyle}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={filters.name}
                    onChange={(e) => handleFilterChange(e, 'name')}
                    sx={searchInputStyle}
                    InputProps={searchInputProps}
                    placeholder="Search"
                  />
                </TableCell>
                {/* Mobile filter */}
                <TableCell sx={filterCellStyle}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={filters.mobile}
                    onChange={(e) => handleFilterChange(e, 'mobile')}
                    sx={searchInputStyle}
                    InputProps={searchInputProps}
                    placeholder="Search"
                  />
                </TableCell>
                {/* Aadhar filter */}
                <TableCell sx={filterCellStyle}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={filters.aadhar}
                    onChange={(e) => handleFilterChange(e, 'aadhar')}
                    sx={searchInputStyle}
                    InputProps={searchInputProps}
                    placeholder="Search"
                  />
                </TableCell>
                {/* Registration No filter */}
                <TableCell sx={filterCellStyle}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={filters.registrationNo}
                    onChange={(e) => handleFilterChange(e, 'registrationNo')}
                    sx={searchInputStyle}
                    InputProps={searchInputProps}
                    placeholder="Search"
                  />
                </TableCell>
                {/* Godown Visit Date range filter */}
                <TableCell sx={filterCellStyle}>
                  <Stack direction="row" spacing={0.5}>
                    <TextField
                      size="small"
                      type="date"
                      variant="standard"
                      value={filters.godownVisitDateFrom}
                      onChange={handleGodownDateFromChange}
                      sx={{ ...searchInputStyle, width: '110px' }}
                      InputProps={searchInputProps}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      size="small"
                      type="date"
                      variant="standard"
                      value={filters.godownVisitDateTo}
                      onChange={handleGodownDateToChange}
                      sx={{ ...searchInputStyle, width: '110px' }}
                      InputProps={searchInputProps}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                </TableCell>
                {/* Camp Location filter */}
                <TableCell sx={filterCellStyle}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={filters.campLocation}
                    onChange={(e) => handleFilterChange(e, 'campLocation')}
                    sx={searchInputStyle}
                    InputProps={searchInputProps}
                    placeholder="Search"
                  />
                </TableCell>
                {/* Form Owner filter */}
                <TableCell sx={filterCellStyle}>
                  <TextField
                    size="small"
                    variant="standard"
                    value={filters.formOwner}
                    onChange={(e) => handleFilterChange(e, 'formOwner')}
                    sx={searchInputStyle}
                    InputProps={searchInputProps}
                    placeholder="Search"
                  />
                </TableCell>
                {/* Payment Option filter */}
                <TableCell sx={filterCellStyle}>
                  <Select
                    size="small"
                    variant="standard"
                    value={filters.paymentOption}
                    onChange={(e) => handleFilterChange(e, 'paymentOption')}
                    sx={{ ...searchInputStyle, minWidth: 80 }}
                    displayEmpty
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Online">Online</MenuItem>
                    <MenuItem value="Cash">Cash</MenuItem>
                  </Select>
                </TableCell>
                {/* Empty for Actions */}
                <TableCell sx={filterCellStyle} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}
                >
                  <TableCell>{r.appointmentType}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                  <TableCell>{r.mobile}</TableCell>
                  <TableCell>{r.aadhar}</TableCell>
                  <TableCell>{r.registrationNo}</TableCell>
                  <TableCell>{formatDate(r.godownVisitDate)}</TableCell>
                  <TableCell>{r.campLocation}</TableCell>
                  <TableCell>{r.formOwner}</TableCell>
                  <TableCell>{r.paymentOption}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {isAdmin && (
                        <IconButton size="small" onClick={() => handleToggleLock(r.id)}
                          title={r.unlock_granted_by ? `Unlock granted — click to re-lock` : 'Click to unlock for editing'}
                          sx={{ bgcolor: r.unlock_granted_by ? '#fff3e0' : '#fce4ec', '&:hover': { bgcolor: r.unlock_granted_by ? '#ffe0b2' : '#f8bbd0' } }}>
                          {r.unlock_granted_by ? <LockOpenIcon fontSize="small" color="warning" /> : <LockIcon fontSize="small" color="error" />}
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(r.id)}
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Edit'}
                        sx={{
                          bgcolor: '#e3f2fd',
                          '&:hover': { bgcolor: '#bbdefb' },
                          opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1,
                        }}
                      >
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(r.id)}
                        disabled={!isAdmin && r.is_locked && !r.unlock_granted_by}
                        title={!isAdmin && r.is_locked && !r.unlock_granted_by ? 'Record is locked' : 'Delete'}
                        sx={{
                          bgcolor: '#ffebee',
                          '&:hover': { bgcolor: '#ffcdd2' },
                          opacity: (!isAdmin && r.is_locked && !r.unlock_granted_by) ? 0.4 : 1,
                        }}
                      >
                        {(!isAdmin && r.is_locked && !r.unlock_granted_by) ? <LockIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit Appointment' : 'Add Appointment'}</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl sx={{ flex: '1 1 30%' }}>
                <InputLabel id="appt-type-label">Appointment Type</InputLabel>
                <Select
                  labelId="appt-type-label"
                  label="Appointment Type"
                  name="appointmentType"
                  value={form.appointmentType}
                  onChange={handleChange}
                >
                  <MenuItem value="">-- Select --</MenuItem>
                  {appointmentTypes.map((a) => (
                    <MenuItem key={a.code} value={a.label}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                name="name"
                label="Name"
                value={form.name}
                onChange={handleChange}
                sx={{ flex: '1 1 70%' }}
                required
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  label="Gender"
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="mobile"
                label="Mobile No"
                value={form.mobile}
                onChange={handleChange}
                sx={{ flex: '1 1 auto' }}
              />
              <TextField
                name="whatsappMobile"
                label="WhatsApp Mobile No"
                value={form.whatsappMobile}
                onChange={handleChange}
                sx={{ flex: '1 1 auto' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                name="aadhar"
                label="Aadhar No"
                value={form.aadhar}
                onChange={handleChange}
                sx={{ flex: '1 1 60%' }}
              />
              <TextField
                name="registrationNo"
                label="Registration No"
                value={form.registrationNo}
                onChange={handleChange}
                sx={{ flex: '1 1 40%' }}
              />
            </Box>

            <TextField
              name="other.village"
              label="Village"
              value={form.other.village}
              onChange={handleChange}
              sx={{ flex: '1 1 25%' }}
            />
            <TextField
              name="other.taluka"
              label="Taluka"
              value={form.other.taluka}
              onChange={handleChange}
              sx={{ flex: '1 1 25%' }}
            />
            <TextField
              name="other.district"
              label="District"
              value={form.other.district}
              onChange={handleChange}
              sx={{ flex: '1 1 25%' }}
            />

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                name="appointmentLocation"
                label="Appointment taken location"
                value={form.appointmentLocation}
                onChange={handleChange}
                sx={{ flex: '1 1 50%' }}
              />
              <TextField
                name="campLocation"
                label="Camp Location"
                value={form.campLocation}
                onChange={handleChange}
                sx={{ flex: '1 1 50%' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                name="operatorName"
                label="Appoint.Taken Operator Name"
                value={form.operatorName}
                onChange={handleChange}
                sx={{ flex: '1 1 33%' }}
              />
              <FormControl sx={{ flex: '1 1 33%' }}>
                <InputLabel id="form-owner-label">Form Owner</InputLabel>
                <Select
                  labelId="form-owner-label"
                  label="Form Owner"
                  name="formOwner"
                  value={form.formOwner}
                  onChange={handleChange}
                >
                  <MenuItem value="NITIN SIR">NITIN SIR</MenuItem>
                  <MenuItem value="KUMBHAR MADAM">KUMBHAR MADAM</MenuItem>
                  <MenuItem value="KAMBALE MADAM">KAMBALE MADAM</MenuItem>
                  <MenuItem value="TAMBAVE OFFICE">TAMBAVE OFFICE</MenuItem>
                </Select>
              </FormControl>
              <TextField
                name="karad.reference"
                label="Reference"
                value={form.karad.reference}
                onChange={handleChange}
                sx={{ flex: '1 1 34%' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                name="appointmentDate"
                label="Appointment Date"
                value={form.appointmentDate}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 33%' }}
              />
              <TextField
                name="godownVisitDate"
                label="Godown Visit Date"
                value={form.godownVisitDate}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 33%' }}
              />
              <TextField
                name="godownLocation"
                label="Godown Location"
                value={form.godownLocation}
                onChange={handleChange}
                sx={{ flex: '1 1 34%' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                name="cashAmount"
                label="Cash Amount Rs."
                value={form.cashAmount}
                onChange={handleChange}
                type="number"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ flex: '1 1 33%' }}
              />
              <TextField
                name="onlineAmount"
                label="Online Amount Rs."
                value={form.onlineAmount}
                onChange={handleChange}
                type="number"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                sx={{ flex: '1 1 33%' }}
              />
              <TextField
                name="paymentDate"
                label="Payment Date"
                value={form.paymentDate}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 34%' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}